import React, {createContext, ReactNode, useContext, useEffect, useMemo} from 'react';
import {Theme, useMediaQuery, useTheme} from '@mui/material';

// Define the context type
interface MobileContextType {
    isMobile: boolean;
}

// Create a Context with a default value
const MobileContext = createContext<MobileContextType>({isMobile: false});

// Define the provider's props
interface MobileProviderProps {
    children: ReactNode;
}

// Component that provides the isMobile value
const MobileProvider: React.FC<MobileProviderProps> = ({children}) => {
    const theme = useTheme<Theme>();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    // Reflect mobile/desktop onto the root element as a side effect, and clear it
    // when we cross back to desktop (a resize past the breakpoint) — writing the
    // attribute during render left it stuck once set.
    useEffect(() => {
        if (isMobile) {
            document.documentElement.setAttribute('mobile', "true");
        } else {
            document.documentElement.removeAttribute('mobile');
        }
    }, [isMobile]);

    const value = useMemo(() => ({isMobile}), [isMobile]);

    return (
        <MobileContext.Provider value={value}>
            {children}
        </MobileContext.Provider>
    );
};

// Custom hook to use the isMobile context
const useMobile = (): MobileContextType => useContext(MobileContext);

export {MobileProvider, useMobile};
